import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/MainLayout';
import { Card } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import {
  FileStack, CheckCircle2, AlertTriangle, MailWarning, Gauge, Zap,
  TrendingUp, Building2, Boxes, RefreshCw, ArrowRight,
} from 'lucide-react';
import api from '../../lib/api';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const Kpi = ({ icon: Icon, label, value, tone = 'slate', onClick, testid }) => (
  <Card
    onClick={onClick}
    data-testid={testid}
    className={`p-5 rounded-none border-l-4 transition-all ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''}`}
    style={{ borderLeftColor: `var(--paas-${tone})` }}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-muted-foreground" />
    </div>
    <div className="mt-3 text-3xl font-bold tabular-nums">{value}</div>
  </Card>
);

const Panel = ({ title, icon: Icon, action, children, testid }) => (
  <Card className="p-5 rounded-none" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-amber-600" />
        <h3 className="text-sm font-semibold uppercase tracking-wide">{title}</h3>
      </div>
      {action}
    </div>
    {children}
  </Card>
);

const BarRow = ({ label, value, pct, suffix, testid }) => (
  <div className="mb-3" data-testid={testid}>
    <div className="flex justify-between text-sm mb-1">
      <span className="truncate pr-2 text-foreground/90">{label}</span>
      <span className="tabular-nums text-muted-foreground whitespace-nowrap">{suffix}</span>
    </div>
    <div className="h-2 bg-muted overflow-hidden">
      <div className="h-full bg-amber-500" style={{ width: `${Math.max(2, pct)}%` }} />
    </div>
  </div>
);

export default function PaasDashboard() {
  const navigate = useNavigate();
  const [currentAccount] = useState(() => {
    try { return JSON.parse(localStorage.getItem('current_account') || 'null'); } catch { return null; }
  });
  const accountId = currentAccount?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await api.get(`/paas/dashboard?account_id=${accountId}`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load PaaS dashboard');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  const c = data?.counts || {};
  const spend = data?.top_vendors_by_spend || [];
  const usage = data?.top_vendors_by_usage || [];
  const commodities = data?.commodities || [];
  const demand = data?.demand || {};
  const missing = data?.missing_bills || {};
  const maxSpend = Math.max(1, ...spend.map((v) => v.spend));
  const maxUsage = Math.max(1, ...usage.map((v) => v.energy_kwh));
  const maxComm = Math.max(1, ...commodities.map((v) => v.meters));

  return (
    <MainLayout>
      <style>{`:root{--paas-slate:#475569;--paas-green:#059669;--paas-amber:#d97706;--paas-red:#dc2626;--paas-blue:#2563eb}`}</style>
      <div className="space-y-6" data-testid="paas-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Boxes className="h-6 w-6 text-amber-600" />
              <h1 className="text-2xl font-bold">PaaS Dashboard</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Parsing-as-a-Service overview {currentAccount?.name ? `· ${currentAccount.name}` : ''}
            </p>
          </div>
          <Button variant="outline" className="rounded-none" onClick={load} data-testid="paas-refresh">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi icon={FileStack} label="Bills Received" value={num(c.bills_received)} tone="blue" testid="kpi-received" />
              <Kpi icon={CheckCircle2} label="Parsed" value={num(c.parsed)} tone="green" testid="kpi-parsed" />
              <Kpi icon={AlertTriangle} label="Needs Review" value={num(c.needs_review)} tone="amber"
                   onClick={() => navigate('/paas/upload?review=1')} testid="kpi-validations" />
              <Kpi icon={MailWarning} label="Missing Bills" value={num(missing?.missing?.total ?? missing?.total)} tone="red"
                   onClick={() => navigate('/paas/missing')} testid="kpi-missing" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Panel title="Demand & Power Quality" icon={Gauge}
                     action={<Button variant="ghost" size="sm" className="rounded-none text-amber-600" onClick={() => navigate('/paas/demand')}>View <ArrowRight className="h-3 w-3 ml-1" /></Button>}
                     testid="panel-demand">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div><div className="text-2xl font-bold tabular-nums">{num(demand.demand_meters)}</div><div className="text-xs text-muted-foreground mt-1">Demand meters</div></div>
                  <div><div className="text-2xl font-bold tabular-nums">{demand.peak_demand_max ? num(demand.peak_demand_max) : '—'}</div><div className="text-xs text-muted-foreground mt-1">Peak kW</div></div>
                  <div><div className="text-2xl font-bold tabular-nums">{money(demand.demand_charge_total)}</div><div className="text-xs text-muted-foreground mt-1">Demand $</div></div>
                </div>
              </Panel>

              <Panel title="Commodities" icon={Zap} testid="panel-commodities">
                {commodities.length === 0 ? <p className="text-sm text-muted-foreground">No meter data.</p> :
                  commodities.slice(0, 6).map((cm) => (
                    <BarRow key={cm.commodity} label={cm.commodity} pct={(cm.meters / maxComm) * 100}
                            suffix={`${num(cm.meters)} meters`} testid={`commodity-${cm.commodity}`} />
                  ))}
              </Panel>

              <Panel title="Vendors" icon={Building2} testid="panel-vendors">
                <div className="text-4xl font-bold tabular-nums">{num(data?.vendor_count)}</div>
                <p className="text-xs text-muted-foreground mt-1">distinct vendors parsed</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(data?.vendors || []).slice(0, 10).map((v) => (
                    <span key={v} className="text-xs px-2 py-0.5 bg-muted text-foreground/80 truncate max-w-[160px]">{v}</span>
                  ))}
                </div>
              </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Top Vendors by Spend" icon={TrendingUp} testid="panel-spend">
                {spend.length === 0 ? <p className="text-sm text-muted-foreground">No spend data.</p> :
                  spend.map((v) => (
                    <BarRow key={v.vendor} label={v.vendor} pct={(v.spend / maxSpend) * 100}
                            suffix={`${money(v.spend)} · ${v.bills} bills`} testid={`spend-${v.vendor}`} />
                  ))}
              </Panel>
              <Panel title="Top Vendors by Usage" icon={Gauge} testid="panel-usage">
                {usage.length === 0 ? <p className="text-sm text-muted-foreground">No usage data.</p> :
                  usage.map((v) => (
                    <BarRow key={v.vendor} label={v.vendor} pct={(v.energy_kwh / maxUsage) * 100}
                            suffix={`${num(v.energy_kwh)} · ${v.meters} mtrs`} testid={`usage-${v.vendor}`} />
                  ))}
              </Panel>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
